import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { getRequest, postRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import { PanelMenu } from 'primereact/panelmenu';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { createArrayNavigateHandlers } from '../../utils/arrayNavigate';
import { reloadCrmTradersCatalog } from '../../utils/crmCatalogStore';
import { SaveIcon, AddIcon, CopyIcon, VerticalLineIcon, SaveAndCloseIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon } from '../svg/svgIcons';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';

function toDateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function parseDateOnly(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    if (Number.isNaN(dt.getTime())) return null;
    dt.setHours(0, 0, 0, 0);
    return dt;
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function isActiveByDateWindow(startedAt, endedAt) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = parseDateOnly(startedAt);
  const end = parseDateOnly(endedAt);
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
}

const PAGE_ADD_KEY = 'CustomerTradersAdd';
const MASK_ADD = 'cust010';

function supervisorUserIdsFromRow(row) {
  const list = row?.supervisors;
  if (!Array.isArray(list)) return [];
  return list.map((s) => Number(s?.id)).filter((n) => n > 0);
}

export default function CustomerTradersAdd({ crm_trader, type = 'add' }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const { toast, openFavoriteDialog, setFastView, openChosenNotificationPreferences, setDialogText, setVisible } = useContext(UtilContext);
  const [formType, setFormType] = useState(type);
  const [closeTab, setCloseTab] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const submitRef = useRef(null);
  const users = useMemo(() => (Array.isArray(state.users) ? state.users : []), [state.users]);
  const catalogRows = useMemo(
    () => (Array.isArray(state.crm_traders) ? state.crm_traders : []),
    [state.crm_traders],
  );

  useEffect(() => {
    setFormType(type);
  }, [type]);

  const userOptions = useMemo(() => {
    return users.map((u) => ({
      label: u?.employee?.name || u?.username || `ID ${u?.id}`,
      value: u.id,
      user: u,
    }));
  }, [users]);

  const formik = useFormik({
    initialValues: {
      id: crm_trader?.id || null,
      user_id: crm_trader?.user_id || null,
      active: isActiveByDateWindow(crm_trader?.started_at || null, crm_trader?.ended_at || null),
      started_at: crm_trader?.started_at || null,
      ended_at: crm_trader?.ended_at || null,
      user: crm_trader?.user || null,
      supervisor_user_ids: supervisorUserIdsFromRow(crm_trader),
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.user_id) errors.user_id = t('Common.Required');
      return errors;
    },
    onSubmit: async (values) => {
      setDialogText?.(t('Common.Save'));
      setVisible?.(true);
      try {
        const supervisorUserIds = Array.isArray(values.supervisor_user_ids)
          ? [...new Set(values.supervisor_user_ids.map((v) => Number(v)).filter((n) => n > 0))]
          : [];
        const payload = {
          user_id: values.user_id,
          active: !!values.active,
          started_at: values.started_at || null,
          ended_at: values.ended_at || null,
          supervisor_user_ids: supervisorUserIds,
        };
        if (formType === 'add') {
          const resp = await postRequest('/api/crm_traders', payload, true);
          const created = resp?.data || null;
          formik.setValues((prev) => ({
            ...prev,
            id: created?.id || prev.id,
            active: created?.active ?? prev.active,
            started_at: created?.started_at || prev.started_at,
            ended_at: created?.ended_at || prev.ended_at,
            user_id: created?.user_id || prev.user_id,
            user: created?.user || prev.user,
            supervisor_user_ids: supervisorUserIdsFromRow(created) || prev.supervisor_user_ids,
          }));
          setFormType('edit');
          toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerTradersAdd.Toast.Saved'), life: 2500 });
        } else if (values.id) {
          const resp = await postRequest(`/api/crm_traders/update/${Number(values.id)}`, payload, true);
          const updated = resp?.data || null;
          formik.setValues((prev) => ({
            ...prev,
            active: updated?.active ?? prev.active,
            started_at: updated?.started_at || prev.started_at,
            ended_at: updated?.ended_at || prev.ended_at,
            supervisor_user_ids: supervisorUserIdsFromRow(updated) || prev.supervisor_user_ids,
          }));
          toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('CustomerTradersAdd.Toast.Updated'), life: 2500 });
        }
        await reloadCrmTradersCatalog(dispatch, getRequest).catch(() => {});
        if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: t('ServiceTask.SaveFailed'), life: 3000 });
      } finally {
        setVisible?.(false);
        setCloseTab(false);
      }
    },
  });

  const supervisorSelectOptions = useMemo(() => {
    const traderId = formik.values.user_id != null ? Number(formik.values.user_id) : null;
    return users
      .filter((u) => !traderId || Number(u.id) !== traderId)
      .map((u) => ({
        label: u?.employee?.name || u?.username || `ID ${u?.id}`,
        value: u.id,
      }));
  }, [users, formik.values.user_id]);

  const selectedUser = useMemo(() => {
    if (formik.values.user?.id) return formik.values.user;
    return users.find((u) => u.id === formik.values.user_id) || null;
  }, [users, formik.values.user, formik.values.user_id]);

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = createArrayNavigateHandlers(catalogRows, formik.values?.id, (item) => {
    formik.setValues({
      id: item?.id || null,
      user_id: item?.user_id || null,
      active: isActiveByDateWindow(item?.started_at || null, item?.ended_at || null),
      started_at: item?.started_at || null,
      ended_at: item?.ended_at || null,
      user: item?.user || null,
      supervisor_user_ids: supervisorUserIdsFromRow(item),
    });
    setFormType('edit');
  });

  function openAddNew() {
    dispatch({
      type: 'create_new_tab',
      name: t('CustomerTradersAdd.New'), translationKey: 'CustomerTradersAdd.New', legacyLabel: t('CustomerTradersAdd.New'),
      page: PAGE_ADD_KEY,
      props: { crm_trader: null, type: 'add' },
      mask: MASK_ADD,
    });
  }

  function copyCurrent() {
    dispatch({
      type: 'create_new_tab',
      name: t('CustomerTradersAdd.New'), translationKey: 'CustomerTradersAdd.New', legacyLabel: t('CustomerTradersAdd.New'),
      page: PAGE_ADD_KEY,
      props: {
        crm_trader: {
          ...formik.values,
          id: null,
          user_id: null,
          user: null,
        },
        type: 'add',
      },
      mask: MASK_ADD,
    });
  }

  const canAdd = !!user?.permissions?.includes('add customer_management-setup');
  const canEdit = !!user?.permissions?.includes('edit customer_management-setup');
  const disableSave = formType === 'add' ? !canAdd : !canEdit;
  const navDisabled = formType !== 'edit' || !formik.values?.id || catalogRows.length < 2;
  const submitWithoutClose = () => {
    setCloseTab(false);
    submitRef.current?.click();
  };
  const submitWithClose = () => {
    setCloseTab(true);
    submitRef.current?.click();
  };
  const handleEndedAtChange = (nextValue) => {
    const normalized = nextValue || null;
    const nextActive = isActiveByDateWindow(formik.values.started_at, normalized);
    formik.setValues((prev) => ({
      ...prev,
      ended_at: normalized,
      active: nextActive,
    }));
  };

  const clearEndedAt = () => {
    const nextActive = isActiveByDateWindow(formik.values.started_at, null);
    formik.setValues((prev) => ({ ...prev, ended_at: null, active: nextActive }));
  };

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = [{
    key: '0',
    label: t('ServiceTask.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('Common.Notifications'), icon: 'pi pi-envelope', command: () => openChosenNotificationPreferences?.(PAGE_ADD_KEY), template: itemRenderer },
    ],
  }];

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        formik.handleSubmit(e);
      }}
      className="h-full min-h-0 flex flex-col overflow-hidden"
    >
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" disabled={disableSave} className="disabled:opacity-25" onClick={submitWithClose}><SaveAndCloseIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" disabled={disableSave} className="disabled:opacity-25" onClick={submitWithoutClose}><SaveIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" disabled={!canAdd} className="disabled:opacity-25" onClick={openAddNew}><AddIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" disabled={!canAdd || !formik.values?.id} className="disabled:opacity-25" onClick={copyCurrent}><CopyIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollToFirst}><BackToFirstIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollUp}><BackIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollDown}><ForwardIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={navDisabled} onClick={scrollToLast}><ForwardToLastIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => openFavoriteDialog?.(t('CustomerTradersAdd.FavoriteTitle'), PAGE_ADD_KEY)}><FavoriteIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setFastView?.(PAGE_ADD_KEY)}><PriceIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="card flex justify-content-center z-[100]">
          <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
        </div>
      </div>
      <div className="relative flex flex-col h-[calc(100vh-177px)] overflow-y-auto overflow-x-hidden">
        <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
          <h1 className="text-3xl font-semibold text-left text-zinc-900">
            {t('CustomerTradersAdd.Title')}
          </h1>
          {formType === 'edit' && <div className="text-md font-semibold text-gray-800 mt-1">ID: {formik.values.id}</div>}

          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerTradersAdd.User').toUpperCase()}</label>
            <Dropdown
              className="w-full mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
              value={formik.values.user_id}
              options={userOptions}
              optionLabel="label"
              optionValue="value"
              filter
              filterBy="label"
              pt={{
                input: { className: 'px-4 py-2' },
                trigger: { className: 'w-10' },
              }}
              onChange={(e) => {
                const nextValue = Number(e.value || 0) || null;
                formik.setValues((p) => {
                  const prevSups = p.supervisor_user_ids || [];
                  const nextSups = nextValue ? prevSups.filter((id) => Number(id) !== nextValue) : prevSups;
                  return {
                    ...p,
                    user_id: nextValue,
                    user: userOptions.find((x) => x.value === nextValue)?.user || null,
                    supervisor_user_ids: nextSups,
                  };
                });
              }}
              disabled={formType === 'edit'}
              placeholder={t('CustomerTradersAdd.SelectUser')}
              emptyMessage={t('CustomerTradersList.NoRows')}
            />
          </div>
          {formik.errors.user_id && <div className="text-red-600 text-sm">{formik.errors.user_id}</div>}

          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerTradersList.Col.Name').toUpperCase()}</label>
            <input
              type="text"
              value={selectedUser?.employee?.name || '-'}
              readOnly
              className="block w-full px-4 py-2 mt-2 border rounded-md bg-white"
            />
          </div>

          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerTradersList.Col.Username').toUpperCase()}</label>
            <input
              type="text"
              value={selectedUser?.username || '-'}
              readOnly
              className="block w-full px-4 py-2 mt-2 border rounded-md bg-white"
            />
          </div>

          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerTradersAdd.Supervisors').toUpperCase()}</label>
            <MultiSelect
              className="w-full mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
              value={formik.values.supervisor_user_ids || []}
              options={supervisorSelectOptions}
              optionLabel="label"
              optionValue="value"
              display="comma"
              maxSelectedLabels={4}
              filter
              filterBy="label"
              filterPlaceholder={t('Common.Search')}
              placeholder={t('CustomerTradersAdd.SelectSupervisors')}
              disabled={!formik.values.user_id}
              onChange={(e) => formik.setFieldValue('supervisor_user_ids', e.value || [])}
            />
          </div>

          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerTradersList.Col.StartDate').toUpperCase()}</label>
            <input
              type="date"
              value={toDateInputValue(formik.values.started_at)}
                onChange={(e) => {
                  const nextStartedAt = e.target.value || null;
                  formik.setValues((prev) => ({
                    ...prev,
                    started_at: nextStartedAt,
                    active: isActiveByDateWindow(nextStartedAt, prev.ended_at),
                  }));
                }}
              className="block w-full px-4 py-2 mt-2 border rounded-md focus:border-gray-400"
            />
          </div>

          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('CustomerTradersList.Col.EndDate').toUpperCase()}</label>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="date"
                value={toDateInputValue(formik.values.ended_at)}
                onChange={(e) => handleEndedAtChange(e.target.value)}
                className="block w-full px-4 py-2 border rounded-md focus:border-gray-400"
              />
              <button
                type="button"
                onClick={clearEndedAt}
                className="px-3 py-2 border rounded-md bg-white hover:bg-gray-50"
                title={t('CustomerTradersAdd.ClearEndDate')}
              >
                X
              </button>
            </div>
          </div>

          <div>
            <label className="block text-md font-semibold text-gray-800 mt-2">{t('ServiceTaskTable.Col.State').toUpperCase()}</label>
            <select
              value={formik.values.active ? 'active' : 'inactive'}
              disabled
              className="block w-full px-4 py-2 mt-2 border rounded-md focus:border-gray-400"
            >
              <option value="active">{t('Common.Active')}</option>
              <option value="inactive">{t('Common.Inactive')}</option>
            </select>
            <div className="text-xs text-gray-600 mt-1">
              Stav sa určuje automaticky podľa rozsahu dátumov (začiatok/koniec).
            </div>
          </div>
        </div>
      </div>
      <button type="submit" ref={submitRef} className="hidden" />
    </form>
  );
}

